import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "context/user-context";
import { getGroupes } from "services/apiUtilisateurs";
import { handleApiError } from "utils/errorHandlers";
import { register } from "services/apiAuth";
import { Input } from "./base/Input";
import { Select, Option } from "./base/Select";
import { Checkbox } from "./base/Checkbox";
import { Button } from "./base/Button";
import {PASSWORD_REQUIREMENT, validatePassword} from "../utils/utils";
import {InformationCircleIcon} from "@heroicons/react/24/outline";

interface RegisterFormData {
	nom: string;
	prenom: string;
	telephone?: string;
	email: string;
	password: string;
	password_confirmation: string;
	groupe: number;
	accept_cgu: boolean;
}

export const InscriptionContent = () => {
  const { setUser } = useUser();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState<Record<string, string[]>>({});

	const [registerForm, setRegisterForm] = useState<RegisterFormData>({
		nom: "",
		prenom: "",
		telephone: "",
		email: "",
		password: "",
		password_confirmation: "",
		groupe: 1, //par défaut CHU Rouen
		accept_cgu: false,
	});

	const [groupOptions, setGroupOptions] = useState<Option[]>([])

	useEffect(() => {
		const fetchGroupes = async () => {
			try {
				const data = await getGroupes();
				// @ts-ignore
				setGroupOptions(data.map((currentGroupe) => {
					return { value: currentGroupe.id, label: currentGroupe.groupe }
				}));
			} catch (error: any) {
				setErrors(handleApiError(error));
			} finally {
				setLoading(false);
			}
		};

		fetchGroupes();
	}, []);

	// Gestionnaire dédié au Select (reçoit une string, convertit en number)
	const handleGroupeChange = (value: string) => {
		setRegisterForm((prev) => ({
			...prev,
			groupe: Number(value),
		}));
	};


	const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { name, value } = e.target;
		setRegisterForm((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (registerForm.password !== registerForm.password_confirmation) {
			setErrors(
				{
					password: ['Les mots de passe ne correspondent pas'],
				},
			);
			return;
		}
		const validationErreur = validatePassword(registerForm.password);
		if (validationErreur != null) {
			setErrors({
				password: [validationErreur]
			})
			return;
		}
		if (!registerForm.accept_cgu) {
			setErrors({
				accept_cgu: ['Vous devez accepter les conditions d\'utilisation'],
			});
			return;
		}
		setLoading(true);
		setErrors({});
		try {
			const response = await register({
				prenom: registerForm.prenom,
				nom: registerForm.nom,
				telephone: registerForm.telephone,
				email: registerForm.email,
				password: registerForm.password,
				password_confirmation: registerForm.password_confirmation,
				groupe: registerForm.groupe,
			});
			// Mettre à jour le contexte utilisateur avec les données reçues
			if (response.data?.user) {
				setUser(response.data.user);
			}
			navigate('/');
		} catch (error: any) {
			setErrors(handleApiError(error));
		} finally {
			setLoading(false);
		}
	};

	return (
		<form onSubmit={handleSubmit} className="flex flex-col gap-3">
			<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
				<Input
					label="Nom"
					name="nom"
					type="text"
					value={registerForm.nom}
					onChange={handleChange}
					required
					width="100%"
				/>
				<Input
					label="Prenom"
					name="prenom"
					type="text"
					value={registerForm.prenom}
					onChange={handleChange}
					required
					width="100%"
				/>

				<Input
					label="Téléphone"
					name="telephone"
					type="text"
					value={registerForm.telephone}
					onChange={handleChange}
					width="100%"
				/>

				<div>
					<Input
						label="Email"
						name="email"
						type="email"
						value={registerForm.email}
						onChange={handleChange}
						required
						width="100%"
						error={errors.email && errors.email[0]}
					/>
				</div>

				<div>
					<Input
						label="Mot de passe"
						name="password"
						type="password"
						value={registerForm.password}
						onChange={handleChange}
						required
						width="100%"
					/>
				</div>

				<div>
					<Input
						label="Confirmer le mot de passe"
						name="password_confirmation"
						type="password"
						value={registerForm.password_confirmation}
						onChange={handleChange}
						required
						width="100%"
					/>
				</div>


				<div className="md:col-span-2 text-sm -mt-2 text-gray-500">
					{errors.password && (
						<span className="text-alert text-sm px-2">{errors.password[0]}</span>
					)}
					<div className="flex gap-2"><InformationCircleIcon className="w-4 h-4 mt-1" /><span className="flex-1">{PASSWORD_REQUIREMENT}</span></div>
				</div>

				<div>
					<Select
						label="Groupe"
						name="groupe"
						value={registerForm.groupe}
						options={groupOptions}
						width="100%"
						required
						onChange={(e) => handleGroupeChange(e.target.value)}
					/>
				</div>
			</div>

			<div>
				<div className="flex items-center">
					<Checkbox
						label=""
						id="accept-cgu"
						checked={registerForm.accept_cgu}
						onChange={(checked) => setRegisterForm((prev) => ({
							...prev,
							accept_cgu: checked,
						}))}
					/>
					<label htmlFor="accept-cgu" className="cursor-pointer">J'accepte les
						<a href="/informations-legales?tab=0" className="text-primary-600 hover:underline mx-1" target="_blank">conditions générales d'utilisation</a>
						et <a href="/informations-legales?tab=2" className="text-primary-600 hover:underline mx-1" target="_blank">les politiques de confidentialité</a>
					</label>
				</div>
				{errors.accept_cgu && (
					<span className="text-alert text-sm px-2">{errors.accept_cgu[0]}</span>
				)}
			</div>

			<div className="flex justify-center mt-4">
				<Button type="submit" variant="secondary" disabled={loading}>
					{loading ? 'Veuillez patienter...' : 'Créer mon compte'}
				</Button>
			</div>
		</form>
	)
}